import React, { useEffect, useRef, useState } from "react";
import {
  Row,
  Col,
  Card,
  Image,
  Typography,
  Button,
  Spin,
  Modal,
} from "antd";
import { FilePdfOutlined, FileImageOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import NoDataPage from "../../NoDataPages/NoDataPage";
import GalleryIcon from "../../../assets/images/nurseIcons/gallery.svg";
import useIsMobile from "../../../hooks/useIsMobile";
import { fetchPdfObjectUrl, PdfViewer } from "./PdfMessage";

const getAttachmentFileName = (file) => {
  const raw =
    file?.attachment?.path?.split("/").pop() ||
    file?.attachment?.name ||
    "document.pdf";
  return raw.includes(".") ? raw : `${raw}.pdf`;
};

const GalleryPanel = ({
  galleryImages = [],
  isGalleryLoading,
  visibleCount,
  onLoadMore,
}) => {
  const isCompact = useIsMobile();
  const TILE_W = isCompact ? undefined : 208;
  const TILE_RADIUS = 7.31;
  const TILE_BORDER = 0.73;
  const TILE_PADDING = 0.73;

  const [pdfPreview, setPdfPreview] = useState({
    open: false,
    url: "",
    name: "",
    blobUrl: null,
  });

  const blobUrlRef = useRef(null);
  const pdfRequestIdRef = useRef(0);

  const revokeBlobUrl = () => {
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
  };

  useEffect(
    () => () => {
      pdfRequestIdRef.current += 1;
      if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
    },
    []
  );

  const closePdfPreview = () => {
    pdfRequestIdRef.current += 1;
    revokeBlobUrl();
    setPdfPreview({ open: false, url: "", name: "", blobUrl: null });
  };

  const openPdfDirect = (url) => {
    if (!url) return;
    const opened = window.open(url, "_blank", "noopener,noreferrer");
    if (!opened) {
      window.location.assign(url);
    }
  };

  const triggerDownload = (href, fileName) => {
    const link = document.createElement("a");
    link.href = href;
    link.download = fileName || "document.pdf";
    link.rel = "noopener noreferrer";
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const handlePdfOpen = async (url, fileName) => {
    if (!url) return;
    const requestId = ++pdfRequestIdRef.current;
    revokeBlobUrl();
    setPdfPreview({
      open: true,
      url,
      name: fileName,
      blobUrl: null,
      error: false,
    });

    try {
      const blobUrl = await fetchPdfObjectUrl(url);
      if (requestId !== pdfRequestIdRef.current) {
        URL.revokeObjectURL(blobUrl);
        return;
      }
      blobUrlRef.current = blobUrl;
      setPdfPreview((prev) => ({ ...prev, blobUrl }));
    } catch (err) {
      console.error(err);
      if (requestId === pdfRequestIdRef.current) {
        setPdfPreview((prev) => ({ ...prev, error: true }));
      }
    }
  };

  const attachments = galleryImages.filter(
    (item) => item?.message_type === "attachment" && !item?.is_deleted
  );

  if (isGalleryLoading) {
    return (
      <div className="chat-gallery-loading">
        <Spin />
      </div>
    );
  }

  if (attachments.length === 0) {
    return (
      <Row
        className="chat-gallery-empty"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "60vh",
        }}
      >
        <Col span={24} style={{ textAlign: "center", padding: "20px" }}>
          <NoDataPage
            title="Gallery Empty"
            subtitle="No attachments uploaded yet!"
            image={GalleryIcon}
          />
        </Col>
      </Row>
    );
  }

  return (
    <>
      <Row className="chat-gallery-grid">
        {attachments.slice(0, visibleCount).map((file, index) => {
          const isImage = file?.attachment?.type?.startsWith("image/");
          const isPdf = file?.attachment?.type === "application/pdf";
          const fileName = getAttachmentFileName(file);

          return (
            <Col
              key={file?.id ?? index}
              className="chat-gallery-tile"
              style={
                TILE_W
                  ? {
                      flex: `0 0 ${TILE_W}px`,
                      maxWidth: TILE_W,
                    }
                  : undefined
              }
            >
              <Card
                hoverable={isPdf}
                className={isPdf ? "chat-gallery-pdf-card" : undefined}
                style={{
                  width: TILE_W ?? "100%",
                  height: "auto",
                  borderRadius: TILE_RADIUS,
                  border: `${TILE_BORDER}px solid #EAECF0`,
                  overflow: "hidden",
                }}
                bodyStyle={{
                  padding: TILE_PADDING,
                  textAlign: "center",
                  borderRadius: TILE_RADIUS,
                  backgroundColor: "#fff",
                }}
                onClick={
                  isPdf
                    ? () => handlePdfOpen(file?.attachment_url, fileName)
                    : undefined
                }
              >
                {isImage ? (
                  <Image
                    src={file?.attachment_url}
                    alt={fileName}
                    width="100%"
                    height={isCompact ? 120 : 140}
                    style={{
                      objectFit: "cover",
                      borderRadius: TILE_RADIUS,
                      border: `${TILE_BORDER}px solid #EAECF0`,
                      marginBottom: 8,
                    }}
                  />
                ) : isPdf ? (
                  <>
                    <div className="chat-gallery-pdf-preview">
                      <FilePdfOutlined className="chat-gallery-pdf-icon" />
                    </div>
                    <Typography.Text
                      className="chat-gallery-pdf-name"
                      title={fileName}
                    >
                      {fileName}
                    </Typography.Text>
                  </>
                ) : (
                  <div className="chat-gallery-file-preview">
                    <FileImageOutlined
                      style={{ fontSize: 36, color: "#8c8c8c" }}
                    />
                    <Typography.Text type="secondary">
                      Unsupported file type
                    </Typography.Text>
                  </div>
                )}

                <Typography.Text className="chat-gallery-tile-time">
                  {dayjs(file?.created_at).format("DD MMM, hh:mm A")}
                </Typography.Text>
                <Typography.Text className="chat-gallery-tile-sender" ellipsis>
                  {file?.sender_first_name ?? file?.first_name}{" "}
                  {file?.sender_last_name ?? file?.last_name}
                </Typography.Text>
              </Card>
            </Col>
          );
        })}
      </Row>

      {visibleCount < attachments.length && (
        <div style={{ textAlign: "center", marginTop: 16, paddingBottom: 12 }}>
          <Button type="link" onClick={onLoadMore}>
            View More
          </Button>
        </div>
      )}

      <Modal
        title={pdfPreview.name || "PDF Preview"}
        open={pdfPreview.open}
        onCancel={closePdfPreview}
        footer={
          pdfPreview.blobUrl ? (
            <>
              <Button
                onClick={() =>
                  triggerDownload(
                    pdfPreview.blobUrl,
                    pdfPreview.name || "document.pdf"
                  )
                }
              >
                Download
              </Button>
              <Button
                type="primary"
                onClick={() => openPdfDirect(pdfPreview.blobUrl)}
              >
                Open in browser
              </Button>
            </>
          ) : null
        }
        width={isCompact ? "96%" : "85%"}
        centered
        destroyOnClose
        className="chat-gallery-pdf-modal"
        styles={{
          body: {
            height: isCompact ? "70vh" : "80vh",
            padding: 12,
          },
        }}
      >
        {pdfPreview.blobUrl ? (
          <PdfViewer src={pdfPreview.blobUrl} title={pdfPreview.name} />
        ) : pdfPreview.error ? (
          <Typography.Text type="danger">Failed to load PDF</Typography.Text>
        ) : pdfPreview.open ? (
          <div className="chat-gallery-loading">
            <Spin />
          </div>
        ) : null}
      </Modal>
    </>
  );
};

export default GalleryPanel;
